import React, { createContext, useState, useEffect } from 'react';
import type {
  ChannelOption,
  TacticOption,
  CurrencyOption,
  ChannelTacticConfig
} from '@monthly-data-collection/shared';

interface DemoSpendConfigContextType {
  channels: ChannelOption[];
  tactics: TacticOption[];
  currencies: CurrencyOption[];
  combinations: ChannelTacticConfig[];
  isDemo: boolean;
  seedDemoData: () => void;
  clearDemoData: () => void;
}

const DemoSpendConfigContext = createContext<DemoSpendConfigContextType | undefined>(undefined);

export const DemoSpendConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [tactics, setTactics] = useState<TacticOption[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [combinations, setCombinations] = useState<ChannelTacticConfig[]>([]);
  const [isDemo, setIsDemo] = useState(false);

  // Marketing channels from BI data
  const DEMO_CHANNELS = [
    'Amazon', 'Apple', 'Bing', 'Capterra', 'Criteo', 'Demandbase', 'Direct', 'DV360',
    'Facebook', 'Gartner', 'Gemini', 'Google', 'Hivestack', 'LinkedIn', 'MNTN',
    'NextDoor', 'Outbrain', 'Pinterest', 'Postie', 'Quantcast', 'Quora', 'Reddit',
    'RollWorks', 'SeedTag', 'Snapchat', 'Spotify', 'StackAdapt', 'Taboola', 'Tapjoy',
    'Tiktok', 'TripAdvisor', 'Twitch', 'Twitter', 'Walmart', 'Yahoo', 'Yelp'
  ];

  // Marketing tactics from BI data
  const DEMO_TACTICS = [
    'App Installs', 'Audio', 'Connect', 'CTV', 'Demand Gen', 'Direct Mail',
    'Discovery', 'Display', 'DOOH', 'DSP', 'Local', 'Native', 'OOH', 'PMAX',
    'Search', 'Search Japan', 'Shopping', 'Smart', 'Social', 'Video', 'Yelp'
  ];

  // Currency data
  const DEMO_CURRENCIES = [
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
  ];

  const seedDemoData = () => {
    const now = new Date();

    // Generate channel data
    const channelData = DEMO_CHANNELS.map((channel, index) => ({
      value: channel,
      label: channel,
      isActive: true,
      sortOrder: index + 1,
      createdAt: now,
      updatedAt: now,
    }));

    // Generate tactic data
    const tacticData = DEMO_TACTICS.map((tactic, index) => ({
      value: tactic,
      label: tactic,
      isActive: true,
      sortOrder: index + 1,
      createdAt: now,
      updatedAt: now,
    }));

    // Generate currency data
    const currencyData = DEMO_CURRENCIES.map((currency, index) => ({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      isActive: true,
      sortOrder: currency.code === 'USD' ? 0 : index + 1, // USD first
      createdAt: now,
      updatedAt: now,
    }));

    // Generate some sample combinations
    const combinationData: ChannelTacticConfig[] = [
      {
        id: 'google-search',
        channel: 'Google',
        tactic: 'Search',
        displayName: 'Google Search',
        isActive: true,
        sortOrder: 1,
        description: 'Google search campaigns',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'facebook-social',
        channel: 'Facebook',
        tactic: 'Social',
        displayName: 'Facebook Social',
        isActive: true,
        sortOrder: 2,
        description: 'Facebook social media campaigns',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'google-display',
        channel: 'Google',
        tactic: 'Display',
        displayName: 'Google Display',
        isActive: true,
        sortOrder: 3,
        description: 'Google display network campaigns',
        createdAt: now,
        updatedAt: now,
      },
    ];

    setChannels(channelData);
    setTactics(tacticData);
    setCurrencies(currencyData);
    setCombinations(combinationData);
    setIsDemo(true);

    // Store in localStorage for persistence
    localStorage.setItem('demoSpendConfig', JSON.stringify({
      channels: channelData,
      tactics: tacticData,
      currencies: currencyData,
      combinations: combinationData,
    }));
  };

  const clearDemoData = () => {
    setChannels([]);
    setTactics([]);
    setCurrencies([]);
    setCombinations([]);
    setIsDemo(false);
    localStorage.removeItem('demoSpendConfig');
  };

  // Load demo data from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('demoSpendConfig');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setChannels(data.channels || []);
        setTactics(data.tactics || []);
        setCurrencies(data.currencies || []);
        setCombinations(data.combinations || []);
        setIsDemo(true);
      } catch (error) {
        console.error('Error loading demo spend config:', error);
      }
    }
  }, []);

  const value: DemoSpendConfigContextType = {
    channels,
    tactics,
    currencies,
    combinations,
    isDemo,
    seedDemoData,
    clearDemoData,
  };

  return (
    <DemoSpendConfigContext.Provider value={value}>
      {children}
    </DemoSpendConfigContext.Provider>
  );
};
